'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import SessionIdentityControls from '@/components/shared/SessionIdentityControls';

const NAV_ITEMS = [
  { href: '/commercial/tableau-de-bord', label: 'Tableau de bord' },
  { href: '/commercial/clients', label: 'Clients · CRM' },
  { href: '/commercial/demandes', label: 'Demandes' },
  { href: '/commercial/propositions', label: 'Propositions' },
  { href: '/commercial/devis', label: 'Devis' },
  { href: '/commercial/factures', label: 'Factures' },
  { href: '/commercial/adjustment-reviews', label: 'Ajustements' },
  { href: '/commercial/preclient-journey', label: 'Accès pré-client' },
  { href: '/commercial/approbations', label: 'Validations' },
  { href: '/commercial/proposition-acceptances', label: 'Offres acceptées' },
  { href: '/commercial/production-projects', label: 'Projets' },
];

export default function CommercialHeader() {
  const pathname = usePathname();
  const prefetchProtectedPages = pathname === '/commercial/login' ? false : null;
  const [requestCount, setRequestCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (pathname === '/commercial/login') return;
    const controller = new AbortController();
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetch('/api/commercial/public-project-requests/count', {
        signal: controller.signal,
        cache: 'no-store',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (typeof data?.count === 'number') setRequestCount(data.count);
        })
        .catch(() => {});
    };
    refresh();
    const timer = window.setInterval(refresh, 15000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [pathname]);
  const label = (item: (typeof NAV_ITEMS)[number]) =>
    item.href === '/commercial/demandes' && requestCount > 0
      ? `${item.label} (${requestCount})`
      : item.label;

  return (
    <header className="commercial-header">
      <a className="ch-skip" href="#commercial-content">
        Aller au contenu
      </a>
      <div className="ch-top">
        {/* Brand */}
        <div className="ch-brand-group">
          <Link
            href="/commercial/tableau-de-bord"
            prefetch={prefetchProtectedPages}
            className="ch-brand"
            aria-label="RDV-Partners — Tableau de bord commercial"
          >
            <div className="w-7 h-7 rounded-md bg-[#00d4c8]/20 border border-[#00d4c8]/40 flex items-center justify-center">
              <span className="text-[#00d4c8] text-xs font-bold">R</span>
            </div>
            <span>
              RDV-PARTNERS<small>Espace commercial</small>
            </span>
          </Link>
          <Link href="/direction" className="ch-public ch-direction">
            ← Espace Direction
          </Link>
          <Link href="/" className="ch-public">
            Site public ↗
          </Link>
        </div>
        <SessionIdentityControls space="commercial" variant="compact" />
      </div>
      <nav aria-label="Navigation commerciale" className="ch-desktop-nav">
        {NAV_ITEMS?.map((item) => {
          const active = pathname?.startsWith(item?.href);
          return (
            <Link
              key={item?.href}
              href={item?.href}
              prefetch={prefetchProtectedPages}
              aria-current={active ? 'page' : undefined}
              className={`ch-nav-link focus-visible:outline ${active ? 'is-active' : ''}`}
            >
              {label(item)}
            </Link>
          );
        })}
      </nav>
      <div className="ch-mobile-toolbar">
        <Link href="/commercial/tableau-de-bord" prefetch={prefetchProtectedPages}>
          ⌂ Tableau de bord
        </Link>
        <button
          type="button"
          aria-expanded={menuOpen}
          aria-controls="commercial-mobile-menu"
          onClick={() => setMenuOpen((value) => !value)}
        >
          {menuOpen ? 'Fermer le menu' : 'Parcourir les espaces'} {menuOpen ? '−' : '+'}
        </button>
      </div>
      <nav
        id="commercial-mobile-menu"
        aria-label="Navigation commerciale mobile"
        className={`ch-mobile-nav flex-wrap ${menuOpen ? 'is-open' : ''}`}
      >
        {NAV_ITEMS.map((item) => {
          const active = pathname?.startsWith(item.href);
          return (
            <Link
              key={item.href}
              href={item.href}
              prefetch={prefetchProtectedPages}
              aria-current={active ? 'page' : undefined}
              className={`ch-nav-link focus-visible:outline ${active ? 'is-active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {label(item)}
            </Link>
          );
        })}
      </nav>
    </header>
  );
}

